import { IconEdit, IconX } from '@tabler/icons-react'
import uniqBy from 'lodash/uniqBy'
import { useRef, useState } from 'react'

import { AstraButton, AstraCollapsibleCard, AstraIcon, AstraParagraph, AstraTooltip } from '~/astra'
import { ButtonTag, PractitionerChipWithTooltip } from '~/components'
import { RecurringAssignmentType } from '~/storm'

import { allRecurringAssignmentRoomDays, allRecurringAssignmentStatusDays } from '../../shared'
import { DeleteAssignment } from './DeleteAssignment'
import { EditRecurringAssignment } from './Edit/EditRecurringAssignment'
import { RecurringAssignmentPreview } from './RecurringAssignmentPreview'

type Props = {
    assignment: RecurringAssignmentType
}

export const RecurringAssignment = ({ assignment }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    if (!assignment.practitioner) {
        return null
    }

    const uniqueStatuses = uniqBy(allRecurringAssignmentStatusDays(assignment).filter(Boolean), 'code')
    const uniqueRooms = uniqBy(allRecurringAssignmentRoomDays(assignment).filter(Boolean), 'room_code')

    return (
        <AstraCollapsibleCard
            open={isOpen}
            onOpenChange={setIsOpen}
            onAnimationEnd={() => {
                if (isOpen) {
                    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                }
            }}
            header={
                <div className="flex items-center gap-2.5 pr-4">
                    <PractitionerChipWithTooltip shortName={assignment.practitioner.short_name} />
                    {uniqueStatuses.map(status => (
                        <div className="flex h-8 w-12 items-center" key={status.code}>
                            <ButtonTag colorType={status.color} label={status.label} fillType="all" disableHover />
                        </div>
                    ))}
                    {uniqueRooms.map(room => (
                        <div className="flex h-8 w-12 items-center" key={room.room_code}>
                            <ButtonTag colorType="room" label={room.room_code} fillType="all" disableHover />
                        </div>
                    ))}
                    <AstraTooltip tooltipContent={assignment.title}>
                        <AstraParagraph className="grow truncate">{assignment.title}</AstraParagraph>
                    </AstraTooltip>
                </div>
            }
        >
            <div className="flex justify-between gap-4 rounded-lg py-2" ref={containerRef}>
                {isEditing ? (
                    <EditRecurringAssignment assignment={assignment} onClose={() => setIsEditing(false)} />
                ) : (
                    <RecurringAssignmentPreview assignment={assignment} />
                )}
                <div className="flex gap-1">
                    {isEditing ? (
                        <AstraButton icon={<AstraIcon icon={IconX} size="medium" />} variant="ghost" size="large" onClick={() => setIsEditing(false)} />
                    ) : (
                        <>
                            <AstraButton icon={<AstraIcon icon={IconEdit} size="medium" />} variant="ghost" size="large" onClick={() => setIsEditing(true)} />
                            <DeleteAssignment assignment={assignment} />
                        </>
                    )}
                </div>
            </div>
        </AstraCollapsibleCard>
    )
}
